/** The last-minute window that qualifies a request for the enhanced recommended rate. */
export const ENHANCED_RATE_WINDOW_HOURS = 48;

/**
 * Combines a shift date with a `"h:mm am/pm"` arrival time into a single start `Date`.
 * Mirrors the parsing used on the Review step; returns the date unchanged when the time
 * string does not match, so callers get a defined (never NaN) Date.
 */
export const parseShiftStart = (date: Date, arrivalTime: string): Date => {
  const result = new Date(date);
  const match = arrivalTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);

  if (!match) return result;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toLowerCase();

  if (period === 'pm' && hours !== 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;

  result.setHours(hours, minutes, 0, 0);
  return result;
};

/**
 * True when the request is for exactly one shift (SDB) whose start is still in the future and
 * less than 48 hours away — the condition for defaulting the enhanced recommended rate.
 *
 * Multi-shift (MDB) requests, no dates, or a start 48h+ away all return false.
 */
export const isSingleShiftWithin48h = (
  dates: Date[] | undefined,
  arrivalTime: string | undefined,
  now: Date = new Date(),
): boolean => {
  if (!dates || dates.length !== 1 || !arrivalTime) return false;

  const shiftStart = parseShiftStart(dates[0], arrivalTime);
  const hoursUntilShift = (shiftStart.getTime() - now.getTime()) / (1000 * 60 * 60);

  return hoursUntilShift > 0 && hoursUntilShift < ENHANCED_RATE_WINDOW_HOURS;
};
